import { STATUS_META, STATUS_CODES, FULL_DAY_CODES, HALF_DAY_CODES, parseEntry, entryWeight } from "../lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { useMemo, useState } from "react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function pad2(n) { return n.toString().padStart(2, "0"); }
function isoDate(y, m, d) { return `${y}-${pad2(m)}-${pad2(d)}`; }
function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }
function firstWeekday(y, m) { return new Date(y, m - 1, 1).getDay(); }

function weeklyRowSummary(weekDates, entries) {
  const counts = Object.fromEntries(STATUS_CODES.map(c => [c, 0]));
  let working = 0;
  weekDates.forEach(d => {
    const value = entries[d];
    parseEntry(value).forEach(c => { if (counts[c] != null) counts[c]++; });
    working += entryWeight(value);
  });
  return { counts, working: Math.round(working * 10) / 10 };
}

function DayCellBadge({ value, meta }) {
  const parts = parseEntry(value);
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    const m = STATUS_META[parts[0]] || meta;
    return (
      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded whitespace-nowrap"
        style={{ color: m.color, border: `1px solid ${m.border}` }}>{parts[0]}</span>
    );
  }
  // Combo: render both codes joined visually with a plus.
  return (
    <span className="flex items-center gap-0.5">
      {parts.map((c, i) => {
        const m = STATUS_META[c];
        return (
          <span key={i} className="text-[9px] font-mono px-1 py-0.5 rounded whitespace-nowrap"
            style={{ color: m.color, border: `1px solid ${m.border}` }}>{c}</span>
        );
      })}
    </span>
  );
}

function DayCellLabel({ value }) {
  const parts = parseEntry(value);
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    return <span className="text-[10px] text-foreground/90 leading-tight line-clamp-2">{STATUS_META[parts[0]].label}</span>;
  }
  return (
    <span className="text-[10px] text-foreground/90 leading-tight line-clamp-2">
      {parts.map(c => STATUS_META[c].short).join(" + ")}
    </span>
  );
}

function DayPicker({ date, currentValue, onSet, onClear }) {
  const currentParts = parseEntry(currentValue);
  const currentIsHalf = currentParts.length === 1 && HALF_DAY_CODES.includes(currentParts[0]);
  const currentIsCombo = currentParts.length === 2;
  const [tab, setTab] = useState(currentIsHalf || currentIsCombo ? "half" : "full");
  const [firstHalf, setFirstHalf] = useState(currentIsHalf ? currentParts[0] : currentIsCombo ? currentParts[0] : "");
  const [secondHalf, setSecondHalf] = useState(currentIsCombo ? currentParts[1] : "");

  const applyHalf = (a, b) => {
    if (!a) return;
    if (b && a !== b) onSet([a, b].join("+"));
    else onSet(a);
  };

  return (
    <div className="p-1">
      <div className="flex items-center gap-1 px-1 pb-2">
        <button onClick={() => setTab("full")}
          data-testid={`picker-tab-full-${date}`}
          className={`flex-1 text-[11px] font-mono uppercase tracking-wider px-2 py-1.5 rounded transition-colors ${tab === "full" ? "bg-blue-500/15 text-blue-600 dark:text-blue-300 border border-blue-500/40" : "text-muted-foreground hover:bg-muted/50 border border-transparent"}`}>Full Day</button>
        <button onClick={() => setTab("half")}
          data-testid={`picker-tab-half-${date}`}
          className={`flex-1 text-[11px] font-mono uppercase tracking-wider px-2 py-1.5 rounded transition-colors ${tab === "half" ? "bg-blue-500/15 text-blue-600 dark:text-blue-300 border border-blue-500/40" : "text-muted-foreground hover:bg-muted/50 border border-transparent"}`}>Half Day</button>
      </div>

      {tab === "full" && (
        <div className="grid grid-cols-1 gap-1">
          {FULL_DAY_CODES.map(c => {
            const m = STATUS_META[c];
            const active = currentValue === c;
            return (
              <button key={c} onClick={() => onSet(c)}
                data-testid={`status-option-${date}-${c}`}
                className={`flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-muted/50 transition-colors ${active ? "ring-1 ring-blue-500/60" : ""}`}>
                <span className="font-mono text-xs px-1.5 py-0.5 rounded shrink-0" style={{ color: m.color, border: `1px solid ${m.border}`, backgroundColor: m.bg }}>{c}</span>
                <span className="text-xs text-foreground/90">{m.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {tab === "half" && (
        <div className="space-y-2">
          <div>
            <div className="px-1 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">First half</div>
            <div className="grid grid-cols-2 gap-1">
              {HALF_DAY_CODES.map(c => {
                const m = STATUS_META[c];
                const active = firstHalf === c;
                return (
                  <button key={c} onClick={() => setFirstHalf(c === firstHalf ? "" : c)}
                    data-testid={`half-first-${date}-${c}`}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left transition-colors ${active ? "bg-blue-500/10 ring-1 ring-blue-500/60" : "hover:bg-muted/50"}`}>
                    <span className="font-mono text-[10px] px-1 py-0.5 rounded shrink-0" style={{ color: m.color, border: `1px solid ${m.border}`, backgroundColor: m.bg }}>{c}</span>
                    <span className="text-[11px] text-foreground/90 leading-tight">{m.short}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="px-1 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">Second half <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span></div>
            <div className="grid grid-cols-2 gap-1">
              {HALF_DAY_CODES.filter(c => c !== firstHalf).map(c => {
                const m = STATUS_META[c];
                const active = secondHalf === c;
                return (
                  <button key={c} onClick={() => setSecondHalf(c === secondHalf ? "" : c)}
                    disabled={!firstHalf}
                    data-testid={`half-second-${date}-${c}`}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-blue-500/10 ring-1 ring-blue-500/60" : "hover:bg-muted/50"}`}>
                    <span className="font-mono text-[10px] px-1 py-0.5 rounded shrink-0" style={{ color: m.color, border: `1px solid ${m.border}`, backgroundColor: m.bg }}>{c}</span>
                    <span className="text-[11px] text-foreground/90 leading-tight">{m.short}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={() => applyHalf(firstHalf, secondHalf)}
            disabled={!firstHalf}
            data-testid={`half-apply-${date}`}
            className="w-full text-xs font-mono uppercase tracking-wider py-2 rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 disabled:cursor-not-allowed">
            Apply
          </button>
        </div>
      )}

      {currentValue && (
        <button onClick={onClear}
          data-testid={`clear-${date}`}
          className="w-full mt-2 text-xs text-red-400 hover:text-red-300 py-1.5 border-t border-border">Clear entry</button>
      )}
    </div>
  );
}

export default function TimesheetCalendar({ year, month, entries, onChange, locked }) {
  const total = daysInMonth(year, month);
  const firstDow = firstWeekday(year, month);

  const weeks = useMemo(() => {
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const w = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    return w;
  }, [year, month, total, firstDow]);

  const setValue = (date, value) => {
    if (locked) return;
    if (entries[date] === "HO") return; // company holiday — immutable
    const next = { ...entries };
    if (value === null || value === "") delete next[date]; else next[date] = value;
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[repeat(7,minmax(0,1fr))_220px] bg-muted/30 border-b border-border">
        {DAYS.map((d) => (
          <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">{d}</div>
        ))}
        <div className="px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 font-mono border-l border-border">Weekly Summary</div>
      </div>

      {weeks.map((week, wi) => {
        const dates = week.filter(Boolean).map(d => isoDate(year, month, d));
        const { counts, working } = weeklyRowSummary(dates, entries);
        return (
          <div key={wi} className="grid grid-cols-[repeat(7,minmax(0,1fr))_220px] border-b border-border last:border-b-0">
            {week.map((d, di) => {
              if (!d) return <div key={di} className="aspect-square sm:aspect-auto sm:h-24 bg-muted/40" />;
              const date = isoDate(year, month, d);
              const value = entries[date];
              const parts = parseEntry(value);
              const primaryMeta = parts.length ? STATUS_META[parts[0]] : null;
              const isHoliday = value === "HO";
              const cellDisabled = locked || isHoliday;
              if (cellDisabled) {
                return (
                  <div key={di}
                    data-testid={`day-cell-${date}`}
                    className="relative h-24 border-r border-border last:border-r-0 p-2 flex flex-col justify-between cursor-not-allowed"
                    style={primaryMeta ? { backgroundColor: primaryMeta.bg, borderTop: `2px solid ${primaryMeta.border}` } : {}}
                    title={isHoliday ? "Company Holiday — locked" : "Timesheet locked"}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-xs font-mono text-muted-foreground">{pad2(d)}</span>
                      <DayCellBadge value={value} meta={primaryMeta} />
                    </div>
                    <DayCellLabel value={value} />
                    {isHoliday && <span className="text-[9px] text-muted-foreground/70 font-mono">🔒 locked</span>}
                  </div>
                );
              }
              return (
                <Popover key={di}>
                  <PopoverTrigger asChild>
                    <button
                      data-testid={`day-cell-${date}`}
                      className="cell-pop relative h-24 border-r border-border last:border-r-0 p-2 text-left flex flex-col justify-between disabled:cursor-not-allowed group"
                      style={primaryMeta ? { backgroundColor: primaryMeta.bg, borderTop: `2px solid ${primaryMeta.border}` } : {}}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <span className="text-xs font-mono text-muted-foreground group-hover:text-foreground">{pad2(d)}</span>
                        <DayCellBadge value={value} meta={primaryMeta} />
                      </div>
                      {value ? <DayCellLabel value={value} /> : <span className="text-[10px] text-muted-foreground/60 group-hover:text-muted-foreground">Click to set</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 bg-popover border-border p-2" data-testid={`day-popover-${date}`}>
                    <DayPicker
                      date={date}
                      currentValue={value || ""}
                      onSet={(v) => setValue(date, v)}
                      onClear={() => setValue(date, null)}
                    />
                  </PopoverContent>
                </Popover>
              );
            })}
            <div className="border-l border-border p-3 bg-muted/20">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">Week {wi + 1}</div>
              <div className="mt-1 text-2xl font-display font-light text-blue-600 dark:text-blue-400">{working}<span className="text-xs text-muted-foreground ml-1">days</span></div>
              <div className="mt-2 flex flex-wrap gap-1">
                {STATUS_CODES.filter(c => counts[c] > 0).map(c => (
                  <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                    style={{ color: STATUS_META[c].color, backgroundColor: STATUS_META[c].bg, border: `1px solid ${STATUS_META[c].border}` }}>
                    {c}·{counts[c]}
                  </span>
                ))}
                {Object.values(counts).every(v => v === 0) && <span className="text-[10px] text-muted-foreground/60">—</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
